import $ from 'jquery';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useRef, useState, useContext } from "react";
import { GdkMultipleSelectBox } from "@gdk/multiple-select-box";
import useBranchOptions from '../../hooks/useBranchOptions';
import { COUNTRYCODE_OPTIONS, LANGUAGE_OPTIONS } from "@geicoap/ia-shared";
import useContactMethodOptions from '../../hooks/useContactMethodOptions';
import useContentPreferenceOptions from "../../hooks/useContentPreferencesOptions";
import { useMask } from '@react-input/mask';
import { updateAgentProfile } from '../../api/agent-profile';
import { v4 } from 'uuid';
import { classes } from '../../util/style-utils';
import { GdkTooltip } from '@gdk/tooltip';
import { EMAIL_WITH_TLD_REGEX, US_PHONE_REGEX, NON_US_PHONE_REGEX } from '../../constants/ValidationConstants';
import { standardizeAddress } from '../../api/spectrum-address';
import useYesNoOptions from '../../hooks/useYesNoOptions';
import { ClientConfigurationContext } from '../../context/ClientConfigurationContextProvider';
import FloridaCountyOptions from '../GeaoOnboarding/constants/options/FloridaCountyOptions';

/**
 * Helper function to generate form field class names with optional error styling.
 * Reduces cognitive complexity by encapsulating the error class logic.
 * @param {string} baseClasses - The base CSS classes for the form field
 * @param {boolean} hasError - Whether the field has a validation error
 * @returns {string} The combined class names
 */
const formFieldClasses = (baseClasses, hasError) => classes(baseClasses, hasError && "form-field--error");

const US_COUNTRY_CODE = "+1";
const MAX_FOREIGN_PHONE_DIGITS = 13;
const ZIP_CODE_MAX_LENGTH_BY_COUNTRY = { US: 5, MX: 5, IN: 6 };

const normalizePhoneNumber = (value) => (value ?? "").replace(/\D/g, "").slice(0, MAX_FOREIGN_PHONE_DIGITS);

const getCountryCodeDefaultValue = (countryCode) => {
    const optionExists = COUNTRYCODE_OPTIONS.some(option => option.value === countryCode);
    return optionExists ? countryCode : US_COUNTRY_CODE;
};


const validationSchema = yup.object({
    agencyBranch: yup.string().nullable().optional(),
    businessPhoneCountryCode: yup.string().nullable().optional(),
    businessPhone: yup.string().nullable().when("businessPhoneCountryCode", {
        is: (countryCode) => !countryCode || countryCode === US_COUNTRY_CODE,
        then: schema => schema.matches(US_PHONE_REGEX, "Enter a complete phone number."),
        otherwise: schema => schema.matches(NON_US_PHONE_REGEX, "Enter a complete phone number.")
    }).optional(),
    businessPhoneExt: yup.string().nullable().matches(/^\d*$/, "Enter a number.").optional(),
    mobilePhoneCountryCode: yup.string().nullable().optional(),
    mobilePhone: yup.string().nullable().when("mobilePhoneCountryCode", {
        is: (countryCode) => !countryCode || countryCode === US_COUNTRY_CODE,
        then: schema => schema.matches(US_PHONE_REGEX, "Enter a complete phone number."),
        otherwise: schema => schema.matches(NON_US_PHONE_REGEX, "Enter a complete phone number.")
    }).optional(),
    residentPhoneCountryCode: yup.string().nullable().optional(),
    residentPhone: yup.string().nullable().when("residentPhoneCountryCode", {
        is: (countryCode) => !countryCode || countryCode === US_COUNTRY_CODE,
        then: schema => schema.matches(US_PHONE_REGEX, "Enter a complete phone number."),
        otherwise: schema => schema.matches(NON_US_PHONE_REGEX, "Enter a complete phone number.")
    }).optional(),
    faxNumber: yup.string().nullable().length(14, "Enter a complete fax number.").optional(),
    personalEmail: yup.string().nullable()
        .email("Please enter a valid email address")
        .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address")
        .optional(),
    languages: yup.array().nullable().optional(),
    contactMethods: yup.array().nullable().optional(),
    contentPreferences: yup.array().nullable().optional(),
    residentAddressLine1: yup.string().required("Please enter the first line of the resident address."),
    residentAddressLine2: yup.string().nullable().optional(),
    residentAddressCity: yup.string().required("Please enter the resident city."),
    residentAddressState: yup.string().required("Please enter the resident state.").max(2, "State must be two letters.").matches(/[a-zA-Z]+/g,
        { message: "State must contain only letters.", excludeEmptyString: true }),
    residentAddressCountry: yup.string().nullable().optional(),
    residentAddressZip: yup.string().required("Please enter the resident ZIP code."),
    mailingAddressLine1: yup.string().nullable().optional(),
    mailingAddressLine2: yup.string().nullable().optional(),
    mailingAddressCity: yup.string().nullable().optional(),
    mailingAddressState: yup.string().nullable().optional(),
    mailingAddressZip: yup.string().nullable().optional(),
    residentAddressCounty: yup.string().nullable().optional()
});

export default function AgentProfileForm({
    agentProfile = {},
    setAgentProfile = () => { },
    expandableForm,
    expandableFormId = ""
}) {
    const { register, handleSubmit, watch, formState: { errors }, reset, getValues, setValue } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const clientConfiguration = useContext(ClientConfigurationContext);
    const enableOffshore = clientConfiguration?.enableOffshore;
    const isConciergeJobProfile = (agentProfile?.jobProfile || "").toLowerCase() === "concierge";
    const countryOptions = clientConfiguration?.countries || [];
    const stateRegions = clientConfiguration?.stateRegions || {};
    const branchOptions = useBranchOptions(agentProfile?.agency?.agencyId);
    const contactMethodOptions = useContactMethodOptions();
    const contentPreferencesOptions = useContentPreferenceOptions();
    const acceptStandardizedAddressOptions = useYesNoOptions();
    const [standardizeAddressResponse, setStandardizeAddressResponse] = useState(null);

    const watchAcceptStandardizedAddress = watch('acceptStandardizedAddress');
    const defaultResidentAddressCountry = agentProfile.addresses?.residentAddress?.country ?? "US";
    const watchResidentAddressCountry = watch('residentAddressCountry', defaultResidentAddressCountry);
    const residentAddressStateOptions = stateRegions[watchResidentAddressCountry || "US"] || [];
    const residentAddressZipMaxLength = ZIP_CODE_MAX_LENGTH_BY_COUNTRY[watchResidentAddressCountry || "US"] || 9;
    const watchResidentAddressState = watch('residentAddressState', agentProfile.addresses?.residentAddress?.state);

    const defaultBusinessPhoneCountryCode = enableOffshore ? getCountryCodeDefaultValue(agentProfile?.phones?.businessCountryCode) : US_COUNTRY_CODE;
    const watchBusinessPhoneCountryCode = watch('businessPhoneCountryCode', defaultBusinessPhoneCountryCode);
    const isUsBusinessCountryCode = !enableOffshore || watchBusinessPhoneCountryCode === US_COUNTRY_CODE;

    const defaultMobilePhoneCountryCode = enableOffshore ? getCountryCodeDefaultValue(agentProfile?.phones?.cellCountryCode) : US_COUNTRY_CODE;
    const watchMobilePhoneCountryCode = watch('mobilePhoneCountryCode', defaultMobilePhoneCountryCode);
    const isUsMobileCountryCode = !enableOffshore || watchMobilePhoneCountryCode === US_COUNTRY_CODE;

    const defaultResidentPhoneCountryCode = enableOffshore ? getCountryCodeDefaultValue(agentProfile?.phones?.residentCountryCode) : US_COUNTRY_CODE;
    const watchResidentPhoneCountryCode = watch('residentPhoneCountryCode', defaultResidentPhoneCountryCode);
    const isUsResidentCountryCode = !enableOffshore || watchResidentPhoneCountryCode === US_COUNTRY_CODE;

    useEffect(() => {
        if (!enableOffshore) {
            setValue("businessPhoneCountryCode", US_COUNTRY_CODE, { shouldDirty: true, shouldValidate: true });
            setValue("mobilePhoneCountryCode", US_COUNTRY_CODE, { shouldDirty: true, shouldValidate: true });
            setValue("residentPhoneCountryCode", US_COUNTRY_CODE, { shouldDirty: true, shouldValidate: true });
            setValue("residentAddressCountry", "US", { shouldDirty: true, shouldValidate: true });
        }
    }, [enableOffshore, setValue]);

    const previousResidentAddressCountryRef = useRef(watchResidentAddressCountry);

    useEffect(() => {
        const countryChanged = previousResidentAddressCountryRef.current !== watchResidentAddressCountry;
        if (enableOffshore && countryChanged) {
            setValue("residentAddressLine1", "", { shouldDirty: true, shouldValidate: true });
            setValue("residentAddressLine2", "", { shouldDirty: true, shouldValidate: true });
            setValue("residentAddressCity", "", { shouldDirty: true, shouldValidate: true });
            setValue("residentAddressState", "", { shouldDirty: true, shouldValidate: true });
            setValue("residentAddressZip", "", { shouldDirty: true, shouldValidate: true });
            setValue("residentAddressCounty", "", { shouldDirty: true, shouldValidate: true });
            setValue("acceptStandardizedAddress", null, { shouldDirty: true, shouldValidate: false });
            setStandardizeAddressResponse(null);
        }
        previousResidentAddressCountryRef.current = watchResidentAddressCountry;
    }, [watchResidentAddressCountry, enableOffshore, setValue, setStandardizeAddressResponse]);

    useEffect(() => {
        const currentZip = getValues("residentAddressZip");
        if (currentZip && currentZip.length > residentAddressZipMaxLength) {
            setValue("residentAddressZip", "", { shouldDirty: true, shouldValidate: true });
        }
    }, [watchResidentAddressCountry, residentAddressZipMaxLength, getValues, setValue]);

    useEffect(() => {
        if (watchAcceptStandardizedAddress === "true" && addressVerificationSucceeded() && standardizeAddressResponse && standardizeAddressResponse.addresses?.length > 0) {
            setValue('residentAddressLine1', standardizeAddressResponse.addresses[0].street);
            setValue('residentAddressLine2', standardizeAddressResponse.addresses[0].street2);
            setValue('residentAddressCity', standardizeAddressResponse.addresses[0].city);
            setValue('residentAddressState', standardizeAddressResponse.addresses[0].state);
            setValue('residentAddressZip', standardizeAddressResponse.addresses[0].zipCode?.zip5);
        }
    }, [watchAcceptStandardizedAddress]);

    const getMultiSelectValues = (id) => $(`#${id}`).select2("data").map(d => d.id);

    const [enableButtons, setEnableButtons] = useState(true);

    const buildResidentAddress = (values) => {
        const shouldUseStandardizedAddress =
            values.acceptStandardizedAddress === "true" &&
            addressVerificationSucceeded() &&
            standardizeAddressResponse;

        const residentAddressCountry = enableOffshore
            ? (values.residentAddressCountry ?? "US")
            : (agentProfile.addresses?.residentAddress?.country ?? "US");

        if (shouldUseStandardizedAddress) {
            return {
                ...agentProfile.addresses.residentAddress,
                street1: standardizeAddressResponse.addresses[0].street,
                street2: standardizeAddressResponse.addresses[0].street2,
                city: standardizeAddressResponse.addresses[0].city,
                state: standardizeAddressResponse.addresses[0].state,
                zipCode: standardizeAddressResponse.addresses[0].zipCode.zip5,
                country: residentAddressCountry,
                county: values.residentAddressCounty
            };
        }

        return {
            ...agentProfile.addresses.residentAddress,
            street1: values.residentAddressLine1,
            street2: values.residentAddressLine2,
            city: values.residentAddressCity,
            state: values.residentAddressState,
            zipCode: values.residentAddressZip,
            country: residentAddressCountry,
            county: values.residentAddressCounty
        };
    };

    const onSubmit = (values) => {
        if (values.errors) {
            return;
        }

        const residentAddress = buildResidentAddress(values);
        const updatedAgentProfile = {
            ...agentProfile,
            agency: {
                ...agentProfile.agency,
                branchId: values.agencyBranch,
            },
            phones: {
                ...agentProfile.phones,
                business: values.businessPhone,
                businessCountryCode: enableOffshore ? values.businessPhoneCountryCode : US_COUNTRY_CODE,
                businessExtension: values.businessPhoneExt,
                cell: values.mobilePhone,
                cellCountryCode: enableOffshore ? values.mobilePhoneCountryCode : US_COUNTRY_CODE,
                resident: values.residentPhone,
                residentCountryCode: enableOffshore ? values.residentPhoneCountryCode : US_COUNTRY_CODE,
                fax: values.fax
            },
            addresses: {
                ...agentProfile.addresses,
                residentAddress: residentAddress,
                mailingAddress: {
                    street1: values.mailingAddressLine1,
                    street2: values.mailingAddressLine2,
                    city: values.mailingAddressCity,
                    state: values.mailingAddressState,
                    zipCode: values.mailingAddressZip
                }
            },
            email: values.personalEmail,
            languages: getMultiSelectValues(languagesSelectId.current),
            contactMethods: getMultiSelectValues(contactMethodsSelectId.current),
            contentPreferences: getMultiSelectValues(contentPreferencesSelectId.current)
        };

        setEnableButtons(false);

        updateAgentProfile(updatedAgentProfile,
            () => {
                setAgentProfile(updatedAgentProfile);
                setStandardizeAddressResponse(null);
                expandableForm?.closeExpandedForm(document.getElementById(expandableFormId));
            },
            () => {
                setEnableButtons(true);
            });
    }

    const onCancel = () => { reset(); setStandardizeAddressResponse(null); };

    const handleBusinessPhoneChange = (event) => {
        if (!isUsBusinessCountryCode) {
            event.target.value = normalizePhoneNumber(event.target.value);
        }
    };

    const handleBusinessPhoneCountryCodeChange = () => {
        setValue("businessPhone", "", { shouldDirty: true, shouldValidate: true });
    };

    const { ref: registerBusinessPhoneRef, ...registerBusinessPhone } = register("businessPhone", {
        value: isUsBusinessCountryCode ? (agentProfile.phones?.business ?? "") : normalizePhoneNumber(agentProfile.phones?.business),
        onChange: handleBusinessPhoneChange,
        setValueAs: value => isUsBusinessCountryCode ? value : normalizePhoneNumber(value)
    });

    const { ref: registerBusinessPhoneCountryCodeRef, ...registerBusinessPhoneCountryCode } = register("businessPhoneCountryCode", {
        value: defaultBusinessPhoneCountryCode,
        onChange: handleBusinessPhoneCountryCodeChange
    });

    const maskBusinessPhoneRef = useMask({ mask: '(___) ___-____', replacement: { _: /\d/ } });

    const maskMobilePhoneRef = useMask({ mask: '(___) ___-____', replacement: { _: /\d/ } });

    const handleMobilePhoneChange = (event) => {
        if (!isUsMobileCountryCode) {
            event.target.value = normalizePhoneNumber(event.target.value);
        }
    };

    const handleMobilePhoneCountryCodeChange = () => {
        setValue("mobilePhone", "", { shouldDirty: true, shouldValidate: true });
    };

    const { ref: registerMobilePhoneRef, ...registerMobilePhone } = register("mobilePhone", {
        value: isUsMobileCountryCode ? (agentProfile.phones?.cell ?? "") : normalizePhoneNumber(agentProfile.phones?.cell),
        onChange: handleMobilePhoneChange,
        setValueAs: value => isUsMobileCountryCode ? value : normalizePhoneNumber(value)
    });

    const { ref: registerMobilePhoneCountryCodeRef, ...registerMobilePhoneCountryCode } = register("mobilePhoneCountryCode", {
        value: defaultMobilePhoneCountryCode,
        onChange: handleMobilePhoneCountryCodeChange
    });

    const handleResidentPhoneChange = (event) => {
        if (!isUsResidentCountryCode) {
            event.target.value = normalizePhoneNumber(event.target.value);
        }
    };

    const handleResidentPhoneCountryCodeChange = () => {
        setValue("residentPhone", "", { shouldDirty: true, shouldValidate: true });
    };

    const { ref: registerResidentPhoneRef, ...registerResidentPhone } = register("residentPhone", {
        value: isUsResidentCountryCode ? (agentProfile.phones?.resident ?? "") : normalizePhoneNumber(agentProfile.phones?.resident),
        onChange: handleResidentPhoneChange,
        setValueAs: value => isUsResidentCountryCode ? value : normalizePhoneNumber(value)
    });

    const { ref: registerResidentPhoneCountryCodeRef, ...registerResidentPhoneCountryCode } = register("residentPhoneCountryCode", {
        value: defaultResidentPhoneCountryCode,
        onChange: handleResidentPhoneCountryCodeChange
    });

    const maskResidentPhoneRef = useMask({ mask: '(___) ___-____', replacement: { _: /\d/ } });

    const languagesSelectId = useRef(`languages-${v4()}`);
    const contactMethodsSelectId = useRef(`contact-${v4()}`);
    const contentPreferencesSelectId = useRef(`contentPreferences-${v4()}`);

    useEffect(() => { new GdkMultipleSelectBox({ content: `#${languagesSelectId.current}` }); }, []);
    useEffect(() => { new GdkMultipleSelectBox({ content: `#${contactMethodsSelectId.current}` }); }, []);
    useEffect(() => { new GdkMultipleSelectBox({ content: `#${contentPreferencesSelectId.current}` }); }, []);

    useEffect(() => { $(`#${languagesSelectId.current}`).trigger("change"); });
    useEffect(() => { $(`#${contactMethodsSelectId.current}`).trigger("change"); });
    useEffect(() => { $(`#${contentPreferencesSelectId.current}`).trigger("change"); });

    useEffect(() => { reset(); setStandardizeAddressResponse(null) }, [agentProfile, reset]);

    const mobilePhoneTriggerIdRef = useRef(`tooltip-trigger-${v4()}`);
    const homePhoneTriggerIdRef = useRef(`tooltip-trigger-${v4()}`);

    useEffect(() => { new GdkTooltip({ content: `#${mobilePhoneTriggerIdRef.current}` }); }, []);
    useEffect(() => { new GdkTooltip({ content: `#${homePhoneTriggerIdRef.current}` }); }, []);

    const residentAddressOnBlur = () => {
        if (enableOffshore && watchResidentAddressCountry !== "US") {
            return;
        }

        standardizeAddress({
            address: {
                street: getValues("residentAddressLine1"),
                street2: getValues("residentAddressLine2"),
                city: getValues("residentAddressCity"),
                state: getValues("residentAddressState"),
                zipCode: getValues("residentAddressZip")
            }
        },
            response => {
                setStandardizeAddressResponse({ ...response, agentId: agentProfile.id });
            });
    }

    const formatStandardizedAddress = (address) => [
        address?.street,
        address?.street2,
        address?.city && address?.city + ",",
        address?.state,
        address?.zipCode.zip5
    ].join(" ");

    const residentAddressMatchesStandardizedAddress = () => {
        return standardizeAddressResponse?.addresses[0].street === getValues("residentAddressLine1") &&
            standardizeAddressResponse?.addresses[0].street2 === getValues("residentAddressLine2") &&
            standardizeAddressResponse?.addresses[0].city === getValues("residentAddressCity") &&
            standardizeAddressResponse?.addresses[0].state === getValues("residentAddressState") &&
            standardizeAddressResponse?.addresses[0].zipCode.zip5 === getValues("residentAddressZip")
    };

    const addressVerificationSucceeded = () => {
        return standardizeAddressResponse?.addressVerificationStatus === "Success" &&
            standardizeAddressResponse?.addresses[0].confidenceLevel === 100 &&
            standardizeAddressResponse?.agentId === agentProfile.id;
    }

    const privacyPolicyTooltip = () => {
        return (
            <>
                <div tabIndex="0" style={{ fontSize: '12px' }}>
                    At GEICO, we understand the importance of your privacy and security.
                    The information you provide will be kept confidential and secure.
                    It will not be sold or redistributed.
                    To learn more about how we protect your personal information,
                    please review our privacy & security policy:
                    <span> </span>
                    <a href="#">
                        Privacy Policy
                    </a>
                </div>
                <button className="icon-close" aria-label="Close tooltip" />
            </>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} >
            <div className="form-field">
                <label htmlFor="agent-id" className="text">Agent ID</label>
                <input id="agent-id" type="text" size="15" value={agentProfile.id ?? ""} disabled />
            </div>

            <div className={formFieldClasses("form-field", errors.agencyBranch)}>
                <label htmlFor="agency-branch" className="text">Agency Branch</label>
                <div className="select-box">
                    <select id="agency-branch" disabled={!clientConfiguration?.enableEditAgencyBranch}  {...register("agencyBranch", { value: agentProfile.agency?.branchId ?? "" })}>
                        <option value="" key="none" >No branch</option>
                        {branchOptions?.map(option => (
                            <option value={option.value} key={option.value} >{`${option.value} - ${option.text}`}</option>
                        ))}
                    </select>
                </div>
                {errors.agencyBranch &&
                    <span className="form-message">{errors.agencyBranch.message}</span>
                }
            </div>

            <div className={formFieldClasses("form-field row", errors.businessPhone || errors.businessPhoneExt)}>
                {enableOffshore &&
                    <div className="col-sm-12 col-md-6 col-lg-4">
                        <label htmlFor="business-phone-country-code" className="text">Country Code</label>
                        <div className="select-box">
                            <select
                                disabled
                                id="business-phone-country-code"
                                ref={registerBusinessPhoneCountryCodeRef}
                                {...registerBusinessPhoneCountryCode}
                            >
                                {COUNTRYCODE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                }
                <div className={enableOffshore ? "col-sm-12 col-md-6 col-lg-4" : "col-sm-12 col-md-6 col-lg-6"}>
                    <label htmlFor="business-phone" className="text">Business Number</label>
                    {isUsBusinessCountryCode ? (
                        <input
                            disabled
                            key="business-phone-us-mask"
                            id="business-phone"
                            type="text"
                            className="phone-number"
                            ref={(element) => {
                                registerBusinessPhoneRef(element);
                                maskBusinessPhoneRef.current = element;
                            }}
                            {...registerBusinessPhone}
                        />
                    ) : (
                        <input
                            disabled
                            key="business-phone-intl"
                            id="business-phone"
                            type="text"
                            className="phone-number"
                            inputMode="numeric"
                            maxLength={MAX_FOREIGN_PHONE_DIGITS}
                            ref={registerBusinessPhoneRef}
                            {...registerBusinessPhone}
                        />
                    )}
                    {errors.businessPhone &&
                        <span className="form-message">{errors.businessPhone.message}</span>
                    }
                </div>
                <div className={enableOffshore ? "col-sm-12 col-md-6 col-lg-4" : "col-sm-12 col-md-6 col-lg-6"}>
                    <label className="text" htmlFor="business-phone-ext" >Ext.</label>
                    <input disabled id="business-phone-ext" type="text" {...register("businessPhoneExt", { value: agentProfile.phones?.businessExtension ?? "" })} />
                    {errors.businessPhoneExt &&
                        <span className="form-message">{errors.businessPhoneExt.message}</span>
                    }
                </div>
            </div>

            <div className={formFieldClasses("form-field", errors.mobilePhone)}>
                <div className="tooltip-label-container">
                    <label id="label-mobile-phone" htmlFor="mobile-phone" className="text label--inline">Mobile Phone</label>
                    <div className="tooltip-container">
                        <a href="#" id={mobilePhoneTriggerIdRef.current} className="tooltip-trigger" data-tooltip-view="tooltip-view-privacy-policy" aria-labelledby="label-mobile-phone" >
                            <span className="icon-question-mark"></span>
                        </a>
                    </div>
                </div>
                <div id="tooltip-view-privacy-policy" className="tooltip">
                    {privacyPolicyTooltip()}
                </div>
                <div className="row">
                    {enableOffshore &&
                        <div className="col-sm-12 col-md-6 col-lg-4">
                            <label htmlFor="mobile-phone-country-code" className="text">Country Code</label>
                            <div className="select-box">
                                <select
                                    id="mobile-phone-country-code"
                                    ref={registerMobilePhoneCountryCodeRef}
                                    {...registerMobilePhoneCountryCode}
                                >
                                    {COUNTRYCODE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    }
                    <div className={enableOffshore ? "col-sm-12 col-md-6 col-lg-8" : "col-sm-12"}>
                        <label htmlFor="mobile-phone" className="text">Mobile Number</label>
                        <div className="form-field" style={{ marginBottom: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isUsMobileCountryCode ? (
                                    <input
                                        key="mobile-phone-us-mask"
                                        id="mobile-phone"
                                        type="text"
                                        className="phone-number"
                                        ref={(element) => {
                                            registerMobilePhoneRef(element);
                                            maskMobilePhoneRef.current = element;
                                        }}
                                        {...registerMobilePhone}
                                    />
                                ) : (
                                    <input
                                        key="mobile-phone-intl"
                                        id="mobile-phone"
                                        type="text"
                                        className="phone-number"
                                        inputMode="numeric"
                                        maxLength={MAX_FOREIGN_PHONE_DIGITS}
                                        ref={registerMobilePhoneRef}
                                        {...registerMobilePhone}
                                    />
                                )}
                            </div>
                        </div>
                        {errors.mobilePhone &&
                            <span className="form-message">{errors.mobilePhone.message}</span>
                        }
                    </div>
                </div>
            </div>

            <div className={formFieldClasses("form-field", errors.residentPhone)}>
                <div className="tooltip-label-container">
                    <label id="label-resident-phone" htmlFor="resident-phone" className="text label--inline">Home Phone</label>
                    <div className="tooltip-container">
                        <a href="#" id={homePhoneTriggerIdRef.current} className="tooltip-trigger" data-tooltip-view="tooltip-view-privacy-policy" aria-labelledby="label-resident-phone" >
                            <span className="icon-question-mark"></span>
                        </a>
                    </div>
                </div>
                <div id="tooltip-view-privacy-policy" className="tooltip">
                    {privacyPolicyTooltip()}
                </div>
                <div className="row">
                    {enableOffshore &&
                        <div className="col-sm-12 col-md-6 col-lg-4">
                            <label htmlFor="resident-phone-country-code" className="text">Country Code</label>
                            <div className="select-box">
                                <select
                                    id="resident-phone-country-code"
                                    ref={registerResidentPhoneCountryCodeRef}
                                    {...registerResidentPhoneCountryCode}
                                >
                                    {COUNTRYCODE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    }
                    <div className={enableOffshore ? "col-sm-12 col-md-6 col-lg-8" : "col-sm-12"}>
                        <label htmlFor="resident-phone" className="text">Home Number</label>
                        {isUsResidentCountryCode ? (
                            <input
                                key="resident-phone-us-mask"
                                id="resident-phone"
                                type="text"
                                className="phone-number"
                                ref={(element) => {
                                    registerResidentPhoneRef(element);
                                    maskResidentPhoneRef.current = element;
                                }}
                                {...registerResidentPhone}
                            />
                        ) : (
                            <input
                                key="resident-phone-intl"
                                id="resident-phone"
                                type="text"
                                className="phone-number"
                                inputMode="numeric"
                                maxLength={MAX_FOREIGN_PHONE_DIGITS}
                                ref={registerResidentPhoneRef}
                                {...registerResidentPhone}
                            />
                        )}
                    </div>
                </div>
                {errors.residentPhone &&
                    <span className="form-message">{errors.residentPhone.message}</span>
                }
            </div>

            <div className="form-field row">
                <div className="col-sm-12 col-md-7 col-lg-4">
                    <label htmlFor="fax-number" className="text">Fax Number</label>
                    <input
                        disabled
                        id="fax-number"
                        type="text"
                        className="phone-number"
                        value={agentProfile.phones?.fax ?? ""}
                    />
                </div>
            </div>

            <div className="form-field large-text-input">
                <label htmlFor="business-email" className="text">Business Email</label>
                <input id="business-email" type="text" size="30" value={agentProfile.businessEmail ?? ""} disabled />
            </div>

            <div className={formFieldClasses("form-field large-text-input", errors.personalEmail)}>
                <label htmlFor="personal-email" className="text">Email</label>
                <input id="personal-email" type="email" size="30" {...register("personalEmail", { value: agentProfile.email ?? "" })} />
                {errors.personalEmail &&
                    <span className="form-message">{errors.personalEmail.message}</span>
                }
            </div>

            <div className="form-field large-text-input">
                <label htmlFor="business" className="text">Business Address</label>
                <input id="business" type="text" size="30" value={agentProfile.addresses?.businessAddress?.street1 ?? ""} disabled />
            </div>

            <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressLine1)}>
                <label htmlFor="mailing" className="text">Mailing Address Line 1</label>
                <input id="mailing" type="text" size="30" {...register("mailingAddressLine1",
                    { value: agentProfile.addresses?.mailingAddress?.street1 ?? "" })}
                />
                {errors.mailingAddressLine1 &&
                    <span className="form-message">{errors.mailingAddressLine1.message}</span>
                }
            </div>

            <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressLine2)}>
                <label htmlFor="mailing-AddressLine2" className="text">Mailing Address Line 2</label>
                <input id="mailing-AddressLine2" type="text" size="30" {...register("mailingAddressLine2",
                    { value: agentProfile.addresses?.mailingAddress?.street2 ?? "" })}
                />
                {errors.mailingAddressLine2 &&
                    <span className="form-message">{errors.mailingAddressLine2.message}</span>
                }
            </div>
            <div className="row">
                <div className="col-sm-6">
                    <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressCity)}>
                        <label htmlFor="mailing-city" className="text">Mailing Address City</label>
                        <input id="mailing-city" type="text" size="30" {...register("mailingAddressCity",
                            { value: agentProfile.addresses?.mailingAddress?.city ?? "" })}
                        />
                        {errors.mailingAddressCity &&
                            <span className="form-message">{errors.mailingAddressCity.message}</span>
                        }
                    </div>
                </div>
                <div className="col-sm-6">
                    <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressState)}>
                        <label htmlFor="mailing-state" className="text">Mailing Address State</label>
                        <input id="mailing-state" type="text" size="30" {...register("mailingAddressState",
                            { value: agentProfile.addresses?.mailingAddress?.state ?? "" })}
                        />
                        {errors.mailingAddressState &&
                            <span className="form-message">{errors.mailingAddressState.message}</span>
                        }
                    </div>
                </div>
            </div>
            <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressZip)}>
                <label htmlFor="mailing-zip" className="text">Mailing Address Zip</label>
                <input id="mailing-zip" type="text" size="30" {...register("mailingAddressZip",
                    { value: agentProfile.addresses?.mailingAddress?.zipCode ?? "" })}
                />
                {errors.mailingAddressZip &&
                    <span className="form-message">{errors.mailingAddressZip.message}</span>
                }
            </div>

            {enableOffshore &&
                <div className="form-field">
                    <label htmlFor="resident-address-country" className="text">Country</label>
                    <div className="select-box">
                        <select
                            id="resident-address-country"
                            disabled={!isConciergeJobProfile}
                            {...register("residentAddressCountry", { value: defaultResidentAddressCountry })}
                        >
                            {countryOptions.map(option => (
                                <option value={option.value} key={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            }

            <div className={formFieldClasses("form-field large-text-input", errors.residentAddressLine1)}>
                <label htmlFor="residence" className="text">Residential Address Line 1</label>
                <input id="residence"
                    type="text"
                    size="30"
                    {...register("residentAddressLine1", { value: agentProfile.addresses?.residentAddress?.street1 })}
                    onBlur={residentAddressOnBlur}
                />
                {errors.residentAddressLine1 &&
                    <span className="form-message">{errors.residentAddressLine1.message}</span>
                }
            </div>

            <div className={formFieldClasses("form-field large-text-input", errors.residentAddressLine2)}>
                <label htmlFor="address-line-2" className="text">Residential Address Line 2</label>
                <input
                    id="address-line-2"
                    type="text"
                    size="30"
                    {...register("residentAddressLine2", { value: agentProfile.addresses?.residentAddress?.street2 })}
                    onBlur={residentAddressOnBlur}
                />
                {errors.residentAddressLine2 &&
                    <span className="form-message">{errors.residentAddressLine2.message}</span>
                }
            </div>

            <div className="row">
                <div className="col-sm-6">
                    <div className={formFieldClasses("form-field large-text-input", errors.residentAddressCity)}>
                        <label htmlFor="address-city" className="text">City</label>
                        <input
                            id="address-city"
                            type="text"
                            size="30"
                            {...register("residentAddressCity", { value: agentProfile.addresses?.residentAddress?.city })}
                            onBlur={residentAddressOnBlur}
                        />
                        {errors.residentAddressCity &&
                            <span className="form-message">{errors.residentAddressCity.message}</span>
                        }
                    </div>
                </div>
                <div className="col-sm-6">
                    <div className={formFieldClasses("form-field", errors.residentAddressState)}>
                        <label htmlFor="address-state" className="text">State</label>
                        {enableOffshore ? (
                            <div className="select-box">
                                <select
                                    id="address-state"
                                    {...register("residentAddressState", { value: agentProfile.addresses?.residentAddress?.state ?? "" })}
                                    onBlur={residentAddressOnBlur}
                                >
                                    <option value="">Please Select</option>
                                    {residentAddressStateOptions.map(option => (
                                        <option value={option.value} key={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <input
                                id="address-state"
                                type="text" size="5"
                                {...register("residentAddressState", { value: agentProfile.addresses?.residentAddress?.state })}
                                onBlur={residentAddressOnBlur}
                            />
                        )}
                        {errors.residentAddressState &&
                            <span className="form-message">{errors.residentAddressState.message}</span>
                        }
                    </div>
                </div>
            </div>

            <div className={formFieldClasses("form-field", errors.residentAddressZip)}>
                <label htmlFor="address-zip-code" className="text">ZIP Code</label>
                <input
                    id="address-zip-code"
                    type="text" size="15"
                    maxLength={residentAddressZipMaxLength}
                    {...register("residentAddressZip", { value: agentProfile.addresses?.residentAddress?.zipCode })}
                    onBlur={residentAddressOnBlur}
                />
                {errors.residentAddressZip &&
                    <span className="form-message">{errors.residentAddressZip.message}</span>
                }
            </div>

            <div className={formFieldClasses("form-field", errors.residentAddressCounty)}>
                <label htmlFor="address-county" className="text">County</label>
                {watchResidentAddressState?.toUpperCase() === "FL" ? (
                    <div className="select-box">
                        <select
                            id="address-county"
                            {...register("residentAddressCounty", { value: agentProfile.addresses?.residentAddress?.county ?? "" })}
                        >
                            <option value="">Select a county</option>
                            {FloridaCountyOptions.map(option => (
                                <option value={option.value} key={option.value}>{option.name}</option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <input
                        id="address-county"
                        type="text"
                        size="30"
                        {...register("residentAddressCounty", { value: agentProfile.addresses?.residentAddress?.county ?? "" })}
                    />
                )}
                {errors.residentAddressCounty &&
                    <span className="form-message">{errors.residentAddressCounty.message}</span>
                }
            </div>

            {addressVerificationSucceeded() && !residentAddressMatchesStandardizedAddress() &&
                <fieldset className={formFieldClasses("form-field radio-button-group", errors.acceptStandardizedAddress)}>
                    <legend>Do you accept the Suggested Address? {formatStandardizedAddress(standardizeAddressResponse?.addresses[0])}</legend>
                    <div className="radio-button-wrapper col-2">
                        {acceptStandardizedAddressOptions?.map((option) => {
                            var id = `${option.label}-radio-${v4()}`;
                            return (
                                <div key={id}>
                                    <input
                                        id={id}
                                        type="radio"
                                        value={option.value}
                                        defaultChecked={false}
                                        {...register("acceptStandardizedAddress")}
                                        required
                                    />
                                    <label
                                        className="radio"
                                        htmlFor={id}>
                                        <span>{option.label}</span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </fieldset>
            }

            <div className={formFieldClasses("form-field", errors.languages)}>
                <label htmlFor={languagesSelectId.current} className="text">Languages</label>
                <div>
                    <select multiple id={languagesSelectId.current} {...register("languages", { value: agentProfile.languages ?? [] })} >
                        <option value="" key=""></option>
                        {LANGUAGE_OPTIONS?.map(option => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                {errors.languages &&
                    <span className="form-message">{errors.languages.message}</span>
                }
            </div>

            <div className="form-field large-text-input">
                <label htmlFor="npn" className="text">National Producer Number</label>
                <input id="npn" type="text" size="30" value={agentProfile.nationalProducerNumber ?? ""} disabled />
            </div>

            <div className="form-field large-text-input">
                <label htmlFor="lob-can-sell" className="text">Lines of Business</label>
                <input id="lob-can-sell" type="text" size="30" value={agentProfile.lobCanSell?.join(", ") ?? ""} disabled />
            </div>

            <div className="form-field large-text-input">
                <label htmlFor="license-number" className="text">License Number</label>
                <input id="license-number" type="text" size="30" value={agentProfile?.license?.licenseNumber ?? ""} disabled />
            </div>

            <div className="form-field large-text-input">
                <label htmlFor="license-state" className="text">License State</label>
                <input id="license-state" type="text" size="30" value={agentProfile?.license?.licenseState ?? ""} disabled />
            </div>

            <div className={formFieldClasses("form-field", errors.contactMethods)}>
                <label htmlFor={contactMethodsSelectId.current} className="text">Preferred Contact Methods</label>
                <div>
                    <select multiple id={contactMethodsSelectId.current} {...register("contactMethods", { value: agentProfile.contactMethods ?? [] })} >
                        <option value=""></option>
                        {contactMethodOptions?.map(option => (
                            <option value={option.value} key={option.value} >{option.text}</option>
                        ))}
                    </select>
                </div>
                {errors.contactMethods &&
                    <span className="form-message">{errors.contactMethods.message}</span>
                }
            </div>

            <div className={formFieldClasses("form-field", errors.contentPreferences)}>
                <label htmlFor={contentPreferencesSelectId.current} className="text">Content Preferences</label>
                <div>
                    <select multiple id={contentPreferencesSelectId.current} {...register("contentPreferences", { value: agentProfile.contentPreferences ?? [] })} >
                        <option value=""></option>
                        {contentPreferencesOptions?.map(option => (
                            <option value={option.value} key={option.value} >{option.text}</option>
                        ))}
                    </select>
                </div>
                {errors.contentPreferences &&
                    <span className="form-message">{errors.contentPreferences.message}</span>
                }
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <button
                        type="submit"
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                        disabled={!enableButtons}
                    >
                        <span>Save</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                        onClick={() => onCancel()}
                        disabled={!enableButtons}
                    >
                        <span>Cancel</span>
                    </button>
                </div>
            </div>
        </form>
    );
};

